import Divider from '@/components/base/divider/divider';
import { useBasketQuery } from '@/features/basket/api/query/basket';
import { ActionButtons } from '@/features/navigation/components/fly-basket/action-buttons';
import { EstimatedPrice } from '@/features/navigation/components/fly-basket/estimated-price';
import { Header } from '@/features/navigation/components/fly-basket/header';
import { Overlay } from '@/features/navigation/components/fly-basket/overlay';
import { ProductsList } from '@/features/navigation/components/fly-basket/products-list';
import { usePathname } from '@/navigation';
import { RESERVATION_ROUTES } from '@/routes';
import {
  type FlyBasketStore,
  useFlyBasketStore,
} from '@/store/fly-basket-store';
import { useEventListener } from '@react-hookz/web';
import { clsx } from 'clsx';
import Image from 'next/image';
import tipTop from 'public/images/tip_top.svg';
import { type MouseEvent, useEffect, useRef, useState } from 'react';
import FocusLock, { useFocusScope } from 'react-focus-lock';

const selector = (state: FlyBasketStore) => ({
  close: state.close,
  isOpen: state.isOpen,
});

const ANIMATION_TRANSITION_IN_MS = 300;

export const FlyBasket = () => {
  const pathname = usePathname();
  const enabled = !pathname.includes(RESERVATION_ROUTES.RESERVATION_UPGRADE);

  const { data: basket, isLoading } = useBasketQuery(enabled);
  const ref = useRef<HTMLDivElement>(null);
  const timeout = useRef<NodeJS.Timeout | null>(null);
  const [isBasketVisible, setIsBasketVisible] = useState(false);

  const { close, isOpen } = useFlyBasketStore(selector);
  const { autoFocus } = useFocusScope();

  useEventListener(
    typeof document != 'undefined' ? document : null,
    'click',
    (event: MouseEvent) => {
      if (!ref.current || !isOpen) return;
      if (event.target instanceof Node && ref.current.contains(event.target))
        return;
      close();
    },
  );

  useEffect(() => {
    if (!ref.current) return;
    if (timeout.current) {
      clearTimeout(timeout.current);
    }
    if (isOpen) {
      setIsBasketVisible(true);
      autoFocus();
    } else {
      timeout.current = setTimeout(() => {
        setIsBasketVisible(false);
      }, ANIMATION_TRANSITION_IN_MS);
    }

    return () => {
      if (timeout.current) {
        clearTimeout(timeout.current);
      }
    };
  }, [isOpen]);

  useEffect(() => {
    if (basket && !basket?.products.length) {
      close();
    }
  }, [basket, close]);

  return (
    <div
      className={clsx(
        'pointer-events-none fixed s:absolute top-0 left-0 z-top flex h-dvh w-screen justify-end',
        's:anim-pop-out s:-right-xxl s:top-[44px] s:left-auto s:h-auto s:w-max-form-width s:origin-top-right s:scale-90 s:opacity-0',
        'l:top-[50px] l:right-s',
        isOpen &&
          's:anim-pop-in s:!scale-100 s:!opacity-100 pointer-events-auto',
        !isBasketVisible && 'invisible',
      )}
      data-testid="fly-basket"
      aria-live="polite"
      aria-atomic="true"
      inert={!isOpen}
      role="dialog"
      ref={ref}
    >
      <Overlay />
      <Image
        src={tipTop}
        alt=""
        width={26}
        height={11}
        aria-hidden
        className="pointer-events-none absolute top-[-8px] l:right-m s:right-[85px] z-10 s:block hidden h-[11px] w-[26px]"
      />
      <div
        className={clsx(
          'relative flex h-max w-full flex-col self-end bg-main',
          'anim-move-out s:!translate-y-0 s:h-full translate-y-full s:self-start s:overflow-hidden s:rounded-xl s:border-3 s:border-decorative',
        )}
        style={{
          transform: isOpen ? 'translateY(0)' : 'translateY(100%)',
          transition: isOpen
            ? 'transform 400ms cubic-bezier(0, 0, 0.2, 1)'
            : undefined,
        }}
      >
        <FocusLock
          className="flex h-full w-full flex-col s:self-start self-end "
          disabled={!isOpen}
          group="fly-basket"
        >
          <Header />
          <Divider />
          <div className="s:!h-auto flex max-h-[calc(100vh-60px)] flex-col p-xs s:p-inner xs:p-s supports-[height:100dvh]:max-h-[calc(100dvh-60px)] ">
            <ProductsList products={basket?.products} isLoading={isLoading} />
            <EstimatedPrice
              isLoading={isLoading}
              price={basket?.total_products_amount}
            />
            <ActionButtons />
          </div>
        </FocusLock>
      </div>
    </div>
  );
};
